// Orders Shipped by date (Alex, 2026-10-23): a Poland + Newport Team tile under
// the Warehouse KPIs. One row per date of its OWN range (default: the
// last 7 days, today included; the two date pills on the card change
// it, "Last 7 days" puts it back) — the date as DD/MM/YYYY, the
// Jobalots orders placed that day and how many of them have shipped,
// as "shipped of placed" and a % bar. Newest date first; the footer
// sums the range.
//
// Self-contained like the Planner / Leaders Bonus views: it fetches
// GET /admin/dashboard/shipped-by-date itself (60 s poll, refetch on
// tab focus) and paints the last payload from sessionStorage first so a
// reload does not flash empty. Which warehouse gets the tile is
// helpers/dashboardLayout.ts (showsShippedByDate); DashboardAdmin hands
// it to DashboardTeam as a slot.
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import { useTranslation } from "../i18n";
import {
    EMPTY_SHIPPED_BY_DATE, ShippedByDate, ShippedByDateDay, fetchShippedByDate,
} from "../api/dashboardActivity";
import { KpiRange, clampSpan, lastDaysRange, todayIso } from "./helpers/kpiRange";
import { loadSnapshot, saveSnapshot } from "./helpers/dashboardSnapshot";

export const SHIPPED_BY_DATE_DEFAULT_DAYS = 7;
// The BE caps ?from..?to at 92 days (KPI_MAX_RANGE_DAYS); editing one
// end of a longer range drags the other along rather than failing.
const MAX_RANGE_DAYS = 92;
const POLL_MS = 60_000;
const SNAPSHOT_KIND = "shipped-by-date";

// "2026-10-23" → "23/10/2026" — the format Alex asked for, not the
// browser locale's.
export const fmtDdMmYyyy = (iso: string): string => {
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
};

const fmtInt = (n: number) => n.toLocaleString();
const fmtPct = (pct: number | null) =>
    pct === null ? "—" : `${pct.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;

interface Props {
    warehouse: string;
}

const DashboardShippedByDate = ({ warehouse }: Props) => {
    const { t } = useTranslation();
    const [range, setRange] = useState<KpiRange>(() => lastDaysRange(SHIPPED_BY_DATE_DEFAULT_DAYS));
    const [data, setData] = useState<ShippedByDate>(EMPTY_SHIPPED_BY_DATE);
    const [loaded, setLoaded] = useState(false);
    const today = todayIso();
    const defaultRange = lastDaysRange(SHIPPED_BY_DATE_DEFAULT_DAYS);
    const isDefault = range.from === defaultRange.from && range.to === defaultRange.to;
    const scopeKey = `${warehouse || "all"}:${range.from}:${range.to}`;

    const load = useCallback(async () => {
        try {
            const payload = await fetchShippedByDate(warehouse, range.from, range.to);
            setData(payload);
            setLoaded(true);
            saveSnapshot(SNAPSHOT_KIND, `${warehouse || "all"}:${range.from}:${range.to}`, payload);
        } catch {
            toast.error(t("server_error"), { toastId: "dashboard-shipped-by-date-err" });
        }
    }, [warehouse, range, t]);

    useEffect(() => {
        const restored = loadSnapshot<ShippedByDate>(SNAPSHOT_KIND, scopeKey);
        if (restored) { setData({ ...EMPTY_SHIPPED_BY_DATE, ...restored }); setLoaded(true); } else { setLoaded(false); }
        load();
        const id = window.setInterval(load, POLL_MS);
        const onVisible = () => {
            if (document.visibilityState === "visible") load();
        };
        document.addEventListener("visibilitychange", onVisible);
        return () => {
            window.clearInterval(id);
            document.removeEventListener("visibilitychange", onVisible);
        };
    }, [load, scopeKey]);

    const onRange = (next: KpiRange, edited: "from" | "to") => setRange(clampSpan(next, edited, MAX_RANGE_DAYS));

    // Newest date at the top: today's line is the one people look for.
    const rows: ShippedByDateDay[] = [...data.daily].reverse();

    return (
        <section className="tdash-card tdash-sbd" aria-label={t("dashboard_team_shipped_by_date")}>
            <div className="tdash-sbd-head">
                <div className="tdash-sbd-titles">
                    <h3 className="tdash-card-title">{t("dashboard_team_shipped_by_date")}</h3>
                    <span className="tdash-help">
                        {t("dashboard_team_shipped_by_date_help", { channel: data.channel })}
                    </span>
                </div>
                <div className="dash-scope-right">
                    {!isDefault && (
                        <button
                            type="button"
                            className="tdash-sbd-reset"
                            onClick={() => setRange(lastDaysRange(SHIPPED_BY_DATE_DEFAULT_DAYS))}
                        >
                            {t("dashboard_team_range_last_days", { n: String(SHIPPED_BY_DATE_DEFAULT_DAYS) })}
                        </button>
                    )}
                    <input
                        type="date"
                        className="dash-pill-input"
                        value={range.from}
                        max={range.to}
                        onChange={(e) => e.target.value && onRange({ ...range, from: e.target.value }, "from")}
                        aria-label={t("dashboard_team_range_from")}
                    />
                    <span className="dash-pill-sep">–</span>
                    <input
                        type="date"
                        className="dash-pill-input"
                        value={range.to}
                        min={range.from}
                        max={today}
                        onChange={(e) => e.target.value && onRange({ ...range, to: e.target.value }, "to")}
                        aria-label={t("dashboard_team_range_to")}
                    />
                </div>
            </div>

            {rows.length === 0 ? (
                <div className="tdash-sbd-empty">{loaded ? t("dashboard_team_no_orders_in_range") : "…"}</div>
            ) : (
                <table className="tdash-sbd-table">
                    <thead>
                        <tr>
                            <th scope="col">{t("dashboard_col_date")}</th>
                            <th scope="col">{t("dashboard_col_shipped")}</th>
                            <th scope="col" className="tdash-sbd-pct-col">%</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((d) => (
                            <tr key={d.date}>
                                <td className="tdash-sbd-date">
                                    <span>{fmtDdMmYyyy(d.date)}</span>
                                    {d.date === data.today && (
                                        <span className="tdash-sbd-today">{t("dashboard_team_range_today")}</span>
                                    )}
                                </td>
                                <td className="tdash-sbd-shipped">
                                    <span className="tdash-sbd-shipped-num">{fmtInt(d.shipped)}</span>
                                    <span className="tdash-sbd-shipped-of">
                                        {t("dashboard_team_shipped_of_total", { total: fmtInt(d.total) })}
                                    </span>
                                </td>
                                <td>
                                    <div className="tdash-sbd-pct">
                                        <div className="tdash-sbd-bar" aria-hidden="true">
                                            <div className="tdash-sbd-bar-fill" style={{ width: `${d.pct ?? 0}%` }} />
                                        </div>
                                        <span className="tdash-sbd-pct-num">{fmtPct(d.pct)}</span>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td>{t("dashboard_team_shipped_by_date_total")}</td>
                            <td className="tdash-sbd-shipped">
                                <span className="tdash-sbd-shipped-num">{fmtInt(data.shipped)}</span>
                                <span className="tdash-sbd-shipped-of">
                                    {t("dashboard_team_shipped_of_total", { total: fmtInt(data.total) })}
                                </span>
                            </td>
                            <td>
                                <div className="tdash-sbd-pct">
                                    <div className="tdash-sbd-bar" aria-hidden="true">
                                        <div className="tdash-sbd-bar-fill" style={{ width: `${data.pct ?? 0}%` }} />
                                    </div>
                                    <span className="tdash-sbd-pct-num">{fmtPct(data.pct)}</span>
                                </div>
                            </td>
                        </tr>
                    </tfoot>
                </table>
            )}
        </section>
    );
};

export default DashboardShippedByDate;
